// The whole of admin mode's menu, in the order it renders.
//
// One menu for every admin-mode viewer. `requiresAnyOf` narrows it, mirroring
// the guard on the route each item points at, so an item with no gate targets
// a route with no gate.

import {
  BadgeCheck,
  BadgePercent,
  BarChart3,
  Boxes,
  Briefcase,
  Building2,
  Calendar,
  CalendarRange,
  Clock,
  Contact2,
  DollarSign,
  Eye,
  Gift,
  History,
  Home,
  Layers,
  Leaf,
  Map,
  Megaphone,
  Package,
  Settings as SettingsIcon,
  ShoppingBag,
  Ticket,
  TrendingUp,
  Truck,
  Users,
} from 'lucide-react';

import { GIFT_PASS_BUNDLES_LIST_PATH } from '../../../components/gift-pass-bundles/gift-pass-bundles-paths';
import { CAMPAIGNS_LIST_PATH } from '../../../components/ticket-sales-campaigns/ticket-sales-campaigns-paths';
import { Permission } from '../../../types/permissions';

import { FINANCE_SECTION } from './finance-nav';
import type { NavTree } from './nav-tree.types';

const READ_DASHBOARDS = [Permission.READ_DASHBOARDS, Permission.READ_ALL];
const READ_BOOKINGS = [Permission.READ_BOOKINGS, Permission.READ_ALL];
const READ_CUSTOMERS = [Permission.READ_CUSTOMERS, Permission.READ_ALL];

export const ADMIN_NAV: NavTree = {
  entries: [
    {
      kind: 'section',
      id: 'workshops',
      label: 'Workshops',
      icon: Calendar,
      ready: true,
      home: '/admin/overview',
      alsoMatches: ['/admin/workshops'],
      items: [
        {
          id: 'overview',
          label: 'Overview',
          icon: Home,
          to: '/admin/overview',
          requiresAnyOf: READ_DASHBOARDS,
        },
        {
          id: 'sales',
          label: 'Sales',
          icon: Megaphone,
          to: '/admin/sales',
          requiresAnyOf: READ_DASHBOARDS,
        },
        {
          id: 'teachers',
          label: 'Teachers',
          icon: Users,
          to: '/admin/teachers',
          requiresAnyOf: READ_DASHBOARDS,
        },
        {
          id: 'supplies',
          label: 'Supplies',
          icon: Package,
          to: '/admin/supplies',
          requiresAnyOf: READ_DASHBOARDS,
        },
        {
          id: 'venues',
          label: 'Venues',
          icon: Map,
          to: '/admin/venues',
          // A Regional Director now holds read:venues too, so any of the three
          // opens the page; the write grant stays listed for accounts that
          // hold it without the read one.
          requiresAnyOf: [Permission.READ_VENUES, Permission.WRITE_VENUES, Permission.READ_ALL],
        },
        {
          id: 'regions',
          label: 'Regions',
          icon: Layers,
          to: '/admin/regions',
          requiresAnyOf: [Permission.READ_REGIONS],
        },
        {
          id: 'private-events',
          label: 'Private events',
          icon: Briefcase,
          to: '/admin/private-events',
          requiresAnyOf: READ_DASHBOARDS,
        },
      ],
    },
    {
      kind: 'section',
      id: 'customers',
      label: 'Customers',
      icon: Contact2,
      ready: true,
      home: '/admin/customers',
      items: [
        {
          id: 'list',
          label: 'Customers',
          icon: Contact2,
          to: '/admin/customers',
          requiresAnyOf: READ_CUSTOMERS,
        },
        {
          id: 'bookings',
          label: 'Bookings',
          icon: Ticket,
          to: '/admin/bookings',
          requiresAnyOf: READ_BOOKINGS,
        },
        {
          id: 'needs-review',
          label: 'Needs review',
          icon: Clock,
          to: '/admin/needs-review',
          requiresAnyOf: READ_BOOKINGS,
        },
        // Deliberately not open to `read:all`: RD, Finance and Marketing hold
        // it but must not see balances or ledgers.
        {
          id: 'gift-cards',
          label: 'Gift cards',
          icon: Gift,
          to: '/admin/gift-cards',
          requiresAnyOf: [Permission.READ_GIFT_CARDS],
        },
        {
          id: 'gift-passes',
          label: 'Gift passes',
          icon: BadgeCheck,
          to: '/admin/gift-passes',
          requiresAnyOf: [Permission.READ_GIFT_PASSES],
        },
        {
          id: 'claims',
          label: 'Tree claims',
          icon: ShoppingBag,
          to: '/admin/customers',
          ready: false,
        },
      ],
    },
    {
      kind: 'section',
      id: 'operations',
      label: 'Operations',
      icon: Truck,
      ready: true,
      // Staff, not Venues: Venues is a Workshops page, so landing there would
      // swap the panel out from under anyone who clicked Operations.
      home: '/admin/staff',
      items: [
        { id: 'staff', label: 'Staff', icon: Users, to: '/admin/staff' },
        { id: 'inventory', label: 'Inventory', icon: Package, to: '/admin/inventory' },
        {
          id: 'warehouses',
          label: 'Warehouses',
          icon: Boxes,
          to: '/admin/settings/warehouses',
          requiresAnyOf: [Permission.READ_SETTINGS],
          shortcut: true,
        },
      ],
    },
    {
      kind: 'section',
      id: 'marketing',
      label: 'Marketing',
      icon: Megaphone,
      ready: true,
      home: '/admin/marketing',
      items: [
        {
          id: 'ticket-campaigns',
          label: 'Campaigns',
          icon: BadgePercent,
          to: CAMPAIGNS_LIST_PATH,
          requiresAnyOf: [Permission.READ_TICKET_SALES_CAMPAIGNS],
        },
        {
          id: 'gift-pass-bundles',
          label: 'Gift pass bundles',
          icon: Gift,
          to: GIFT_PASS_BUNDLES_LIST_PATH,
          requiresAnyOf: [Permission.READ_GIFT_PASS_BUNDLES],
        },
        // Ad spend and performance; renamed from "Campaigns" when ticket sales
        // campaigns took that name.
        {
          id: 'ads',
          label: 'Ad performance',
          icon: BarChart3,
          to: '/admin/marketing',
          excludes: [CAMPAIGNS_LIST_PATH, GIFT_PASS_BUNDLES_LIST_PATH],
        },
        { id: 'telemetry', label: 'Widget telemetry', icon: Eye, to: '/admin/data' },
        { id: 'traffic', label: 'Site traffic', icon: TrendingUp, to: '/admin/data', ready: false },
      ],
    },
    FINANCE_SECTION,
    {
      kind: 'section',
      id: 'settings',
      label: 'Settings',
      icon: SettingsIcon,
      ready: true,
      home: '/admin/settings',
      items: [
        {
          id: 'tree-species',
          label: 'Tree Species',
          icon: Leaf,
          to: '/admin/settings/tree-species',
        },
        // Management-only pages are hidden without the write grant rather than
        // shown as a page of controls that do nothing.
        {
          id: 'workshop-types',
          label: 'Workshop Types',
          icon: Layers,
          to: '/admin/settings/workshop-types',
          requiresAnyOf: [Permission.WRITE_WORKSHOPS],
        },
        { id: 'regions', label: 'Regions', icon: Map, to: '/admin/settings/regions' },
        {
          id: 'warehouses',
          label: 'Warehouses',
          icon: Building2,
          to: '/admin/settings/warehouses',
        },
        {
          id: 'catalog',
          label: 'Item Catalog',
          icon: Boxes,
          to: '/admin/settings/catalog',
          tag: 'NEW',
        },
        {
          id: 'pay-rates',
          label: 'Shift pay rates',
          icon: DollarSign,
          to: '/admin/settings/pay-rates',
          tag: 'NEW',
          requiresAnyOf: [Permission.UPDATE_SETTINGS],
        },
        {
          id: 'pay-periods',
          label: 'Pay calendar',
          icon: CalendarRange,
          to: '/admin/settings/pay-periods',
          tag: 'NEW',
          requiresAnyOf: [Permission.UPDATE_SETTINGS],
        },
        { id: 'users', label: 'Users', icon: Users, to: '/admin/settings/users', tag: 'NEW' },
        {
          id: 'audit',
          label: 'Audit log',
          icon: History,
          to: '/admin/settings/audit',
          tag: 'NEW',
          requiresAnyOf: [Permission.READ_AUDIT_LOG],
        },
      ],
    },
  ],
  // Three, not four: Search takes the middle slot, and five is what the bar fits.
  bottomTabs: [
    {
      id: 'overview',
      label: 'Overview',
      icon: Home,
      to: '/admin/overview',
      requiresAnyOf: READ_DASHBOARDS,
    },
    {
      id: 'bookings',
      label: 'Bookings',
      icon: Ticket,
      to: '/admin/bookings',
      requiresAnyOf: READ_BOOKINGS,
    },
    {
      id: 'customers',
      label: 'Customers',
      icon: Contact2,
      to: '/admin/customers',
      requiresAnyOf: READ_CUSTOMERS,
    },
  ],
};
